// https://theme-ui.com/theme-spec

/* Misc Notes:
  1. heading refers to h1, h2, h3, etc. Not the header/footer
  10. This file is executed once Serverside, and once Clientside
*/

/////    Brand Specific Color Settings    /////

const primaryPalette = {
  grays: {
    white: '#ffffff',
    black: '#000000',
    gray20: '#f7f7f7',
    gray30: '#dddddd',
    gray40: '#bfbfbf',
    gray50: '#868686',   // Header Nav a
    gray60: '#666666',
    gray70: '#4e4e4e',
  },
  primaries: {
    blue: '#0626a9',
    navy: '#001d48',
  },
  secondaries: {
    brightBlue: '#0084d6',
    orange: '#d44316',
    green: '#00825b'
    
  },
  tertiary: {
    red: '#a01a00',
    yellow: '#e0a529',
    grey: '#d5d7d7'
  },
  scents: {
    spring: '#c9d296',
    stone: '#92c1e9',
    fig: '#e79993',
    redwood: '#fdbe87',
    aspen: '#e4d77e',
    wildlands: '#8fcab9'
  },
  handleColors: {
    oceantop: '#1e4682',
    oceanbottom: '#0f80cf'
  },
  referenceColors: {
    text: '#4e4e4e', // grays.gray70,
    body: '#4e4e4e', // grays.gray70,
    heading: '#001d48', // primaries.navy
    background: '#ffffff', // grays.white,
    primary:  '#d1461c', // primaries_v1.orange50,
    secondary: '#025f9c', // primaries_v1.blue50,
    muted: '#bfbfbf', // grays.gray40,
    navList: "#868686", // grays.gray50
    headerCTA: "#d1461c", // primaries_v1.orange50
    link: '#3ca6e0', // accents.blue40
  }
};

// Theme config exported for access in components, build loop, and builder configs
const styles = {
  referenceColors: primaryPalette.referenceColors,
  breakpoints: ['48em', '64em', '90em'],
  space: {
    none: '0',
    small: ['0.375em', '0.375em', '0.75em'], // Arrays set responsive values from smallest breakpoint to largest
    medium: ['0.5em', '0.5em', '1em'],
    large: ['0.75em', '0.75em', '1.5em'],
    xlarge: ['1.2em', '1.5em', '3em'],
    mega: ['4em', '4em', '8em'],
  },
  sizes: {
    container: ['100%', '43.25em', '61.25em', '76.25em'],
    viewHeight100: '100vh',
    viewWidth100: '100vw',
    navHeight: '70px',
    footerHeight: '38px',
    footerHeightMobile: '105px',
  },
  fonts: {
    body: 'StyreneA, system-ui, sans-serif',
    heading: 'StyreneA, system-ui, sans-serif',
    monospace: 'Menlo, monospace',
    nav: 'TTCommons, system-ui, sans-serif',
  },
  fontSizes: [14, 16, 16, 18, 20, 24, 28, 36, 48],
  fontWeights: {
    body: 400,
    heading: 500,
    bold: 700,
  },
  lineHeights: {
    body: 1.6,
    heading: 1.3,
    button: 1.5,
  },
  letterSpacings: {
    body: '0.05em',
    heading: 0,
    caps: '0.125em',
    caption: '0.0625em',
  },
  // User exposed color options in the Builder UI are also drawn from this theme
  // Color must be defined here to be used in backgroundColors and other sub categories
  colors: { 
    text: primaryPalette.grays.gray70,
    body: primaryPalette.grays.gray70,
    heading: primaryPalette.primaries.navy,
    background: primaryPalette.grays.white,
    primary: primaryPalette.secondaries.blue,
    secondary: primaryPalette.primaries.blue,
    muted: primaryPalette.grays.gray40,
    transparent: 'transparent',
    link: primaryPalette.secondaries.brightBlue,
    // brand palette
    ...primaryPalette.grays,
    ...primaryPalette.accents,
    ...primaryPalette.primaries,
    ...primaryPalette.secondaries,
    ...primaryPalette.tertiary,
    ...primaryPalette.scents,
    ...primaryPalette.handleColors,

    // old colors that are used
    mediumBlue: primaryPalette.primaries.blue,
    darkBlue: primaryPalette.primaries.navy,
    subscriptionBlue: primaryPalette.secondaries.brightBlue,
    orange: primaryPalette.secondaries.orange,
    green: primaryPalette.secondaries.green,
    red: primaryPalette.tertiary.red,
    yellow: primaryPalette.tertiary.yellow,
    freshBlue: primaryPalette.secondaries.brightBlue,
  },
  // Used for component background colors
  backgroundColors: {
    transparent: 'transparent',
    
    ...primaryPalette.grays,
    ...primaryPalette.grays,
    ...primaryPalette.accents,
    ...primaryPalette.primaries,
    ...primaryPalette.secondaries,
    ...primaryPalette.tertiary,
    ...primaryPalette.scents,
    ...primaryPalette.handleColors,
  },
  radii: {
    card: ['0.25em', '0.5em'],
    button: ['0.25rem', '0.25rem'],
  },
  shadows: {
    card: '0 0.125em 0.1875em 0 rgba(0,0,0,0.2)',
  },
  text: {
    body: {
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 'body',
      letterSpacing: 'body',
    },
    bodyLarge: {
      fontFamily: 'body',
      fontWeight: 'body',
      fontSize: 3,
      lineHeight: 1.6,
      letterSpacing: 'body',
    },
    bodyCaps: {
      fontFamily: 'body',
      fontWeight: 'bold',
      fontSize: 0,
      lineHeight: 1.0,
      letterSpacing: 'body',
      textTransform: 'uppercase',
    },
    bodyItalic: {
      fontFamily: 'body',
      fontWeight: 'body',
      fontSize: '11px',
      lineHeight: 1.0,
      letterSpacing: 'body',
      fontStyle: 'italic',
    },
    heading: {
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
      letterSpacing: 'heading',
    },
    headingCaps: {
      variant: 'text.heading',
      fontWeight: 'bold',
      fontSize: 1,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },
  // theme variants
  buttons: {
    primary: {
      bg: primaryPalette.secondaries.orange,
      borderRadius: 'button',
      color: primaryPalette.grays.white,
      fontFamily: 'heading',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: 'bold',
      letterSpacing: 'caps',
      lineHeight: 'button',
      marginBottom: 'medium',
      padding: ['0.75em 3em'],
      textTransform: 'uppercase',
      transition: 'background-color .15s',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      '&:hover': {
        bg: 'orange50d',
      },
      '& + a': {
        marginLeft: ['none', 'none', 'small'],
      },
    },
    secondary: {
      variant: 'buttons.primary',
      bg: primaryPalette.primaries.blue,
      '&:hover': {
        bg: primaryPalette.primaries.navy,
      },
    },
    white: {
      variant: 'buttons.primary',
      bg: 'white',
      '&:hover': {
        bg: 'white',
      },
      color: primaryPalette.primaries.blue
    },
    hollow: {
      variant: 'buttons.primary',
      bg: 'transparent',
      color: primaryPalette.primaries.blue,
      border: '1px solid',
      transition: 'background-color .15s, color .15s',
      '&:hover': {
        bg: primaryPalette.primaries.blue,
        color: 'background',
      },
    },
    link: {
      variant: 'buttons.primary',
      paddingLeft: 0,
      paddingRight: 0,
      bg: 'transparent',
      color: primaryPalette.primaries.blue,
      textDecoration: 'underline',
      '&:hover': {
        bg: 'transparent',
      },
    },
  },
  // markdown styles
  styles: {
    root: {
      scrollBehavior: 'smooth',
      header: {
        fontSize: 1,
      },
      variant: 'text.body',
      h1: {
        variant: 'text.heading',
        fontWeight: 'bold',
        fontSize: [6, 7],
        mt: 0,
        mb: 0,
      },
      h2: {
        variant: 'text.heading',
        fontWeight: 'bold',
        fontSize: [5, 6],
        mt: 0,
        mb: 0,
      },
      h3: {
        variant: 'text.heading',
        fontSize: [4, 5],
        mt: 0,
        mb: 0,
      },
      h4: {
        variant: 'text.heading',
        fontSize: [3, 4],
        mt: 0,
        mb: 0,
      },
      h5: {
        variant: 'text.heading',
        fontSize: 2,
        lineHeight: 1.5,
        mt: 0,
        mb: 0,
      },
      h6: {
        variant: 'text.heading',
        fontWeight: 'heading',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        fontSize: 1,
        lineHeight: 1.5,
        mt: 0,
        mb: 0,
      },
      p: {
        variant: 'text.body',
        mt: 0,
        mb: 0,
      },
      a: {
        color: 'link',
      },
      table: {
        borderCollapse: 'collapse',
        borderSpacing: 0,
      },
      tr: {
        fontSize: '1.1em',
      },
      th: {
        color: 'heading',
        padding: '0.4em',
        borderBottom: '2px solid rgba(0,0,0,0.2)',
        textTransform: 'uppercase',
        verticalAlign: 'middle',
      },
      td: {
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '0.4em',
        lineHeight: '1.8em',
        verticalAlign: 'middle',
        '& p': {
          fontSize: '80%',
          margin: '0.4em 0',
          lineHeight: '1.3',
        },
      },
    },
    textStyles: ['default', 'light', 'dark', 'quote', 'bodyLarge', 'bodyCaps', 'bodyItalic'],
    textColors: ['default', 'white', 'blue', 'navy', 'darkBlue', 'brightBlue', 'orange', 'green', 'red', 'yellow'],
    default: {
      h1: { color: 'heading' },
      h2: { color: 'heading' },
      h3: { color: 'heading' },
      h4: { color: 'heading' },
      h5: { color: 'heading' },
      h6: { color: 'heading' },
    },
    // TODO: Colors should be set using textColors moving forward. 
    light: {
      h1: { color: 'white' },
      h2: { color: 'white' },
      h3: { color: 'white' },
      h4: { color: 'white' },
      h5: { color: 'white' },
      h6: { color: 'white' },
      p: { color: 'white' },
      a: { color: 'white' },
      ul: { color: 'white' },
      ol: { color: 'white' },
      li: { color: 'white' },
    },
    dark: {
      h1: { color: 'heading' },
      h2: { color: 'heading' },
      h3: { color: 'heading' },
      h4: { color: 'heading' },
      h5: { color: 'heading' },
      h6: { color: 'heading' },
      p: { color: 'heading' },
      ul: { color: 'heading' },
      ol: { color: 'heading' },
      li: { color: 'heading' },
    },
    quote: {
      borderLeft: 'solid 0.15rem #185674',
      paddingLeft: '20px',
      color: '#185674',
      lineHeight: '1.5',
      fontStyle: 'italic',
    },
    bodyLarge: {
      p: { fontSize: 3, lineHeight: 1.6 },
    },
    bodyCaps: {
      p: { fontSize: 0, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' },
    },
    bodyItalic: {
      p: { fontSize: '11px', fontStyle: 'italic' },
    },
  },
};
export default styles;